"use client";
import { useCommand } from "../command/command.js";
import { createElement, createHook, forwardRef, useMergeRefs, useTagName } from "@ariakit/react-utils";
import { isButton } from "@ariakit/utils";
import { useEffect, useRef, useState } from "react";
//#region src/button/button.tsx
const TagName = "button";
/**
* Returns props to create a `Button` component. If the element is not a native
* button, the hook will return additional props to make sure it's accessible.
* @see https://ariakit.com/components/button
* @example
* ```jsx
* const props = useButton({ render: <div /> });
* <Role {...props}>Accessible button</Role>
* ```
*/
const useButton = createHook(function useButton(props) {
	const ref = useRef(null);
	const tagName = useTagName(ref, TagName);
	const [isNativeButton, setIsNativeButton] = useState(() => !!tagName && isButton({
		tagName,
		type: props.type
	}));
	useEffect(() => {
		if (!ref.current) return;
		setIsNativeButton(isButton(ref.current));
	}, []);
	props = {
		role: !isNativeButton && tagName !== "a" ? "button" : void 0,
		...props,
		ref: useMergeRefs(ref, props.ref)
	};
	props = useCommand(props);
	return props;
});
/**
* Renders an accessible button element. If the underlying element is not a
* native button, this component will pass additional attributes to make sure
* it's accessible.
* @see https://ariakit.com/components/button
* @example
* ```jsx
* <Button>Button</Button>
* ```
*/
const Button = forwardRef(function Button(props) {
	return createElement(TagName, useButton(props));
});
//#endregion
export { Button, useButton };

//# sourceMappingURL=button.js.map